<script setup lang="ts">
import { type ToastAction } from 'frontend/_stores/toast-store'
import { useCurrentUser } from 'frontend/common/composables/useCurrentUser'
import { useDateFormat } from 'frontend/common/composables/useDateFormat'
import { DateFormatType } from 'frontend/common/date-format/date-format-key'
import { DATE_FORMAT_OPTIONS } from 'frontend/common/date-format/date-format-options'
import { TimeFormatType } from 'frontend/common/date-format/time-format-key'
import { TIME_FORMAT_OPTIONS } from 'frontend/common/date-format/time-format-options'
import Dropdown from 'frontend/common/dropdown.vue'
import EaRadioGroup from 'frontend/common/ea-form-controls/ea-radio-group.vue'
import UserAvatar from 'frontend/common/layout/components/user-avatar.vue'
import { useModals } from 'frontend/common/modals'
import { organizationModuleGuard } from 'frontend/common/module-access-guard'
import { useSignOut } from 'frontend/common/sign-out'
import { UsersService } from 'frontend/users/users-service'
import { computed, inject, ref } from 'vue'
import { useRouter } from 'vue-router'

const { signOut } = useSignOut()
const { formModals } = useModals()
const { selectedTimeFormat, selectedDateFormat, resolvedTimeFormat, resolvedDateFormat } =
  useDateFormat()

function onTimeFormatPick(v: TimeFormatType) {
  selectedTimeFormat.value = v
}
function onDateFormatPick(v: DateFormatType) {
  selectedDateFormat.value = v
}

const timeFormatCollection = computed(() =>
  TIME_FORMAT_OPTIONS.map(opt => ({
    id: opt.value,
    name:
      selectedTimeFormat.value === null && resolvedTimeFormat.value === opt.value
        ? `${opt.label} (Detected)`
        : opt.label,
  })),
)
const dateFormatCollection = computed(() =>
  DATE_FORMAT_OPTIONS.map(opt => ({
    id: opt.value,
    name:
      selectedDateFormat.value === null && resolvedDateFormat.value === opt.value
        ? `${opt.label} (Detected)`
        : opt.label,
  })),
)
const {
  currentUser,
  isImpersonatingOtherUser,
  currentUserAvatarImageUrl,
  currentUserInitials,
  currentUserMail,
} = useCurrentUser()
const router = useRouter()

const mainDomain = inject('$mainDomain')
const showErrorToast: ToastAction = inject('$error')
const showSuccessToast: ToastAction = inject('$success')
const chooseOrganization = inject<(org: { id?: string; name?: string }) => void>(
  'chooseOrganization',
  () => {},
)

const usersService = new UsersService()

const isPasswordChangeModalOpened = ref(false)
const isImpersonateModalOpened = ref(false)

const currentOrganization = computed(() => {
  if (!currentUser.value?.currentOrganizationId) {
    return {}
  } else {
    return (currentUser.value?.organizations || []).find(organization => {
      return organization.id == currentUser.value.currentOrganizationId
    })
  }
})

const canAccessMultipleModules = computed(() => {
  const guard = organizationModuleGuard(
    null,
    currentUser.value?.impersonatedCurrentOrganizationUser ||
      currentUser.value?.currentOrganizationUser,
  )

  return guard.hasMultipleModulesAccess
})

type DropdownAction = {
  type?: string
  title?: string
  subtitle?: string
  href?: string
  target?: string
  clickFn?: (e?: Event, closeFn?: () => void) => void
}

const userDropdownActions = computed(() => {
  if (!currentUser.value) return [] as DropdownAction[]
  const isOrganizationAdmin = currentUser.value.currentOrganizationUser?.isAdmin
  const currentOrganizationHasNoSSO = currentOrganization.value.sso === false
  const isSuperuser = currentUser.value.isSuperuser
  const result: DropdownAction[] = [{ title: currentUser.value.email, type: 'header' }]
  result.push({ type: 'divider', title: '' })
  result.push({ type: 'custom', title: '' })
  if (window.location.host == mainDomain) {
    result.push({ type: 'divider', title: '' })
    result.push({ type: 'header', title: 'Click item to change your context' })
    ;(currentUser.value.organizations || []).forEach(o => {
      result.push({ title: o.name, clickFn: () => chooseOrganization(o) })
    })
  }
  if (isOrganizationAdmin || isSuperuser || canAccessMultipleModules.value) {
    result.push({ type: 'divider', title: '' })
    if (canAccessMultipleModules.value) {
      result.push({ title: 'Back to TMM Hub', href: '/tmm-hub' })
    }
    if (isOrganizationAdmin) {
      result.push({ title: 'Administration', href: '/admin', target: '_blank' })
    }
    if (isSuperuser) {
      result.push({ title: 'Super User Panel', href: '/su', target: '_blank' })
    }
  }
  if (currentOrganizationHasNoSSO) {
    result.push({ type: 'divider', title: '' })
    result.push({
      title: 'Change password',
      clickFn() {
        isPasswordChangeModalOpened.value = true
      },
    })
  }
  if (isOrganizationAdmin) {
    result.push({ type: 'divider', title: '' })
    if (currentUser.value.impersonatedUserId) {
      result.push({
        title: 'Stop impersonation',
        clickFn() {
          stopImpersonation()
        },
      })
    }
    result.push({
      title: `Impersonate ${currentUser.value.impersonatedUserId ? 'another' : 'a'} user`,
      clickFn() {
        isImpersonateModalOpened.value = true
      },
    })
  }
  result.push({ type: 'divider', title: '' })
  result.push({
    title: 'Sign out',
    clickFn: () => {
      doSignOut()
    },
  })
  return result
})

async function doSignOut() {
  await signOut()
  router.push('/')
}

async function stopImpersonation() {
  try {
    await usersService.dispatchAction('impersonate', null, {
      data: {
        impersonatedUserId: null,
      },
      errorHandler() {
        showErrorToast({ message: 'Cannot stop impersonating!' })
      },
    })
    showSuccessToast({ message: 'Impersonation stopped!' })
  } catch (error: unknown) {
    console.error(
      'error happened during impersonating',
      error,
      (error as { response?: unknown })?.response,
    )
  }
}

const currentUserNickLines = computed(() => {
  if (!currentUser.value.name && !currentUser.value.surname) {
    return [currentUser.value?.email?.substring(0, 2)?.toUpperCase() || '??']
  }
  return [currentUser.value.name, currentUser.value.surname]
})
</script>

<template lang="pug">
component(
  v-if="isImpersonateModalOpened"
  :is="formModals.Impersonate"
  :item="currentUser"
  @closed="isImpersonateModalOpened = false"
  name="impersonation-modal"
  size="lg"
)
component(
  v-if="isPasswordChangeModalOpened"
  :is="formModals.ChangePassword"
  @closed="isPasswordChangeModalOpened = false"
  size="md"
)

dropdown(v-if="!!currentOrganization" :actions="userDropdownActions")
  template(#default="{ isOpen }")
    .header-user(:class="{ 'is-open': isOpen }")
      .impersonating-avatar(v-if="isImpersonatingOtherUser")
        i.mi-outlined.mi-supervisor-account
      user-avatar(
        v-else
        :avatar-url="currentUserAvatarImageUrl"
        :initials="currentUserInitials"
        :seed="currentUserMail"
      )
      .nick
        .line(v-for="(line, index) in currentUserNickLines" :key="index") {{ line }}
  template(#customContent)
    .header-date-time-format-panel.px-3.py-2
      .time-format-section.mb-2
        .format-label.fw-bold.mb-1 Time format
        EaRadioGroup(
          :collection="timeFormatCollection"
          :model-value="resolvedTimeFormat"
          @update:model-value="onTimeFormatPick"
          collection-label="name"
          collection-value="id"
          name="header-time-format"
        )
      hr.my-2
      .date-format-section
        .format-label.fw-bold.mb-1 Date format
        EaRadioGroup(
          :collection="dateFormatCollection"
          :model-value="resolvedDateFormat"
          @update:model-value="onDateFormatPick"
          collection-label="name"
          collection-value="id"
          name="header-date-format"
        )
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;

.header-user {
  display: flex;
  cursor: pointer;
  gap: 8px;
  align-items: center;
  padding: 0 8px 0 12px;
  user-select: none;
  height: 48px;
  border-radius: themeVariables.$borderRadius;

  &:hover,
  &.is-open {
    background: map.get(themeVariables.$colors, containerHighest);
  }
}

.impersonating-avatar {
  background: #fff6eb;
  color: #d08219;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.nick {
  text-transform: uppercase;

  .line:first-child {
    text-transform: none;
  }
}

.header-date-time-format-panel {
  font-size: 0.875rem;

  .format-label {
    font-size: 0.875rem;
    color: var(--bs-body-color, #212529);
  }

  .time-format-section,
  .date-format-section {
    :deep(.form-control-container) {
      margin-bottom: 0;
    }
  }
}
</style>
